import { useMemo, useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Calendar as CalendarIcon, Plus, Trash2, CheckCircle2, X, Sparkles } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar as CalendarWidget } from "../components/ui/calendar";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Visit window: November 3 → December 10, 2026 (inclusive), excluding Fri + Sat
const WINDOW_START = new Date(2026, 10, 3); // Month is 0-based: 10 = November
const WINDOW_END = new Date(2026, 11, 10); // 11 = December

const ARABIC_DAYS = ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];
const ARABIC_MONTHS = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];

const toIso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const AUDIENCE_OPTIONS = [
  "أول ثانوي",
  "ثاني ثانوي",
  "ثالث ثانوي",
  "جميع الصفوف",
];

const KhutaSadaDialog = ({ trigger }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // All selectable visit dates (excluding Fri + Sat)
  const allDates = useMemo(() => {
    const items = [];
    const cur = new Date(WINDOW_START);
    while (cur <= WINDOW_END) {
      const dow = cur.getDay(); // 0=Sun..5=Fri, 6=Sat
      if (dow !== 5 && dow !== 6) {
        items.push(new Date(cur));
      }
      cur.setDate(cur.getDate() + 1);
    }
    return items;
  }, []);

  const [visitDate, setVisitDate] = useState(null); // Date
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [takenDates, setTakenDates] = useState(new Set()); // ISO strings (approved — hidden)
  const [regStatus, setRegStatus] = useState(null);
  const stripRef = useRef(null);

  // Dates visible to the user: full window minus admin-approved dates
  const dateList = useMemo(
    () => allDates.filter((d) => !takenDates.has(toIso(d))),
    [allDates, takenDates]
  );

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    participation_type: "individual",
    team_members: [{ name: "", email: "" }],
    is_graduation_project: false,
    graduation_project_name: "",
    work_mode: "solo",
    supervisor_name: "",
    workshop_title: "",
    workshop_kind: "workshop",
    short_description: "",
    target_audience: AUDIENCE_OPTIONS[0],
    objectives: "",
    uniqueness: "",
    student_benefit: "",
    applicant_goal: "",
    additional_notes: "",
    website: "", // honeypot
  });

  // Fetch taken dates + registration status whenever dialog opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const [takenRes, statusRes] = await Promise.all([
          axios.get(`${API}/khuta-sada/taken-dates`),
          axios.get(`${API}/khuta-sada/registration-status`),
        ]);
        if (cancelled) return;
        const list = Array.isArray(takenRes?.data?.dates) ? takenRes.data.dates : [];
        setTakenDates(new Set(list));
        setRegStatus(statusRes?.data || null);
      } catch (_) {
        // non-fatal
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // If the selected date just got hidden (admin approved while dialog was open), clear it
  useEffect(() => {
    if (visitDate && takenDates.has(toIso(visitDate))) {
      setVisitDate(null);
    }
  }, [takenDates, visitDate]);

  // Auto-scroll strip to selected date
  useEffect(() => {
    if (visitDate && stripRef.current) {
      const idx = dateList.findIndex((d) => toIso(d) === toIso(visitDate));
      if (idx > -1) {
        const children = stripRef.current.children;
        if (children[idx]) {
          children[idx].scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
        }
      }
    }
  }, [visitDate, dateList]);

  const updateForm = (patch) => setForm((f) => ({ ...f, ...patch }));

  const addTeamMember = () => {
    updateForm({ team_members: [...form.team_members, { name: "", email: "" }] });
  };
  const removeTeamMember = (i) => {
    const next = form.team_members.filter((_, idx) => idx !== i);
    updateForm({ team_members: next.length ? next : [{ name: "", email: "" }] });
  };
  const updateMember = (i, patch) => {
    const next = [...form.team_members];
    next[i] = { ...next[i], ...patch };
    updateForm({ team_members: next });
  };

  const validate = () => {
    if (!visitDate) return "الرجاء اختيار تاريخ الزيارة.";
    if (!form.full_name.trim()) return "الرجاء إدخال الاسم الكامل.";
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) return "البريد الإلكتروني غير صالح.";
    if (!form.phone.trim() || form.phone.trim().length < 6) return "الرجاء إدخال رقم الواتساب.";
    if (form.participation_type === "team") {
      const complete = form.team_members.filter((m) => m.name.trim() && /^\S+@\S+\.\S+$/.test(m.email));
      if (complete.length < 1) return "يجب إضافة عضوة واحدة على الأقل باسم وبريد صحيحين.";
    }
    if (form.is_graduation_project && !form.graduation_project_name.trim()) return "الرجاء إدخال اسم مشروع التخرج.";
    if (form.work_mode === "supervised" && !form.supervisor_name.trim()) return "الرجاء إدخال اسم المشرفة.";
    if (!form.workshop_title.trim()) return "الرجاء إدخال عنوان الورشة أو البرنامج.";
    if (!form.short_description.trim()) return "الرجاء إدخال وصف مختصر للفكرة.";
    if (!form.objectives.trim()) return "الرجاء إدخال أهداف الورشة.";
    if (!form.uniqueness.trim()) return "الرجاء إدخال ما يميّز الورشة.";
    if (!form.student_benefit.trim()) return "الرجاء إدخال الفائدة للطالبات.";
    if (!form.applicant_goal.trim()) return "الرجاء إدخال هدف المتقدمة من المشاركة.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        visit_date: toIso(visitDate),
        team_members:
          form.participation_type === "team"
            ? form.team_members.filter((m) => m.name.trim() && m.email.trim())
            : [],
      };
      const res = await axios.post(`${API}/khuta-sada/register`, payload);
      // Save tracker marker so returning visitors see tracking instead of register
      const normalizedEmail = form.email.trim().toLowerCase();
      try {
        localStorage.setItem("khuta_sada_email", normalizedEmail);
        window.dispatchEvent(new Event("khuta-sada-registered"));
      } catch (_) {}
      // Close dialog and navigate to the on-site celebration page
      setOpen(false);
      navigate("/khuta-sada/thank-you", {
        state: {
          email: normalizedEmail,
          registration_id: res?.data?.registration_id || null,
          is_waitlist: Boolean(res?.data?.is_waitlist),
        },
      });
    } catch (error) {
      const detail = error?.response?.data?.detail || "تعذّر إرسال الطلب. حاولي مرة أخرى.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setOpen(false);
    setTimeout(() => {
      setSuccess(false);
      setVisitDate(null);
      setForm({
        full_name: "",
        email: "",
        phone: "",
        participation_type: "individual",
        team_members: [{ name: "", email: "" }],
        is_graduation_project: false,
        graduation_project_name: "",
        work_mode: "solo",
        supervisor_name: "",
        workshop_title: "",
        workshop_kind: "workshop",
        short_description: "",
        target_audience: AUDIENCE_OPTIONS[0],
        objectives: "",
        uniqueness: "",
        student_benefit: "",
        applicant_goal: "",
        additional_notes: "",
        website: "",
      });
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className="w-[96vw] max-w-3xl p-0 sm:rounded-2xl rounded-xl overflow-hidden max-h-[95vh] flex flex-col"
        dir="rtl"
        data-testid="khuta-sada-dialog"
      >
        {success ? (
          <div className="text-center px-6 py-10 overflow-y-auto">
            <CheckCircle2 className="mx-auto text-green-600 mb-4" size={64} />
            <h3 className="text-xl font-bold text-[#0B132B] mb-3" style={{ fontFamily: "Cairo" }}>
              تم استلام طلبك بنجاح
            </h3>
            <p className="text-[#334155] leading-loose max-w-md mx-auto">
              شكرًا لتسجيلكِ في برنامج <strong>«للخطى صدى»</strong>. تم استلام طلبكِ، وستتم مراجعته،
              وسيتم التواصل معكِ عند القبول لتأكيد الموعد والتنسيق مع المدرسة.
            </p>
            <Button
              onClick={resetAndClose}
              className="mt-6 bg-[#0B132B] hover:bg-[#1E3A8A] text-white"
              data-testid="khuta-close-success"
            >
              إغلاق
            </Button>
          </div>
        ) : (
          <>
            {/* Formal navy/gold banner header */}
            <div
              className="relative px-5 sm:px-8 py-5 sm:py-6 shrink-0"
              style={{
                background: 'linear-gradient(135deg, #083344 0%, #164E63 100%)',
              }}
            >
              <div
                aria-hidden
                className="absolute inset-0 opacity-20 pointer-events-none"
                style={{
                  backgroundImage:
                    'radial-gradient(circle at 15% 20%, #F5C86B 0%, transparent 35%), radial-gradient(circle at 85% 85%, #F5C86B 0%, transparent 40%)',
                }}
              />
              <div className="relative flex items-center gap-3 sm:gap-4">
                <div
                  className="h-11 w-11 sm:h-14 sm:w-14 rounded-lg flex items-center justify-center shrink-0 ring-2 ring-[#F5C86B]"
                  style={{ background: 'rgba(245,200,107,0.12)' }}
                  aria-hidden
                >
                  <Sparkles size={22} style={{ color: '#F5C86B' }} strokeWidth={1.5} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] sm:text-xs uppercase tracking-[0.25em] font-semibold text-[#F5C86B] mb-1">
                    التسجيل في البرنامج
                  </p>
                  <h2
                    className="text-xl sm:text-2xl font-bold text-white leading-tight truncate"
                    style={{ fontFamily: 'Cairo' }}
                  >
                    للخطى صدى
                  </h2>
                </div>
              </div>
              <p className="relative text-[#CBD5E1] text-xs sm:text-sm mt-3 leading-relaxed">
                اختاري تاريخ الزيارة ثم أكملي البيانات. التاريخ مبدئي حتى يتم التأكيد من قبل المدرسة.
              </p>
            </div>

            {/* Hidden title for a11y (DialogTitle requirement) */}
            <DialogHeader className="sr-only">
              <DialogTitle>التسجيل في برنامج للخطى صدى</DialogTitle>
              <DialogDescription>نموذج التسجيل في فعالية للخطى صدى</DialogDescription>
            </DialogHeader>

            {regStatus && !regStatus.open && (
              <div
                className="mx-5 sm:mx-8 mt-5 rounded-xl px-4 py-3 text-sm text-[#92400E]"
                style={{ background: "#FFFBEB", border: "1px solid #D4AF37" }}
                data-testid="khuta-reg-closed-banner"
              >
                {regStatus.today < regStatus.opens_at
                  ? `يفتح التسجيل بتاريخ ${regStatus.opens_at}`
                  : "انتهت فترة التسجيل لهذا الموسم."}
              </div>
            )}

            <form
              onSubmit={handleSubmit}
              className="space-y-5 sm:space-y-6 px-5 sm:px-8 py-5 sm:py-6 overflow-y-auto flex-1"
              dir="rtl"
            >
              {/* === Visit Date Picker === */}
              <div>
                <Label className="text-[#0B132B] font-semibold mb-3 block text-base">تاريخ الزيارة (مبدئي)</Label>
                <div className="flex gap-2 items-stretch">
                  {/* Calendar icon that opens full calendar */}
                  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="shrink-0 w-12 h-[92px] rounded-xl border border-[#E2E8F0] bg-white hover:bg-slate-50 flex items-center justify-center transition-colors"
                        data-testid="khuta-open-calendar"
                        aria-label="فتح التقويم"
                      >
                        <CalendarIcon size={20} className="text-[#334155]" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarWidget
                        mode="single"
                        selected={visitDate}
                        onSelect={(d) => {
                          if (!d) return;
                          const dow = d.getDay();
                          if (d < WINDOW_START || d > WINDOW_END) return;
                          if (dow === 5 || dow === 6) return;
                          if (takenDates.has(toIso(d))) return;
                          setVisitDate(d);
                          setCalendarOpen(false);
                        }}
                        disabled={(d) => {
                          const dow = d.getDay();
                          return (
                            d < WINDOW_START ||
                            d > WINDOW_END ||
                            dow === 5 ||
                            dow === 6 ||
                            takenDates.has(toIso(d))
                          );
                        }}
                        defaultMonth={WINDOW_START}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>

                  {/* Horizontal scrollable date strip (RTL: oldest on the right, newest on the left) */}
                  <div
                    ref={stripRef}
                    className="flex-1 flex gap-2 overflow-x-auto pb-1 scroll-smooth"
                    style={{ scrollbarWidth: "thin", direction: "rtl" }}
                    data-testid="khuta-date-strip"
                  >
                    {dateList.map((d) => {
                      const iso = toIso(d);
                      const isSel = visitDate && toIso(visitDate) === iso;
                      const dow = d.getDay();
                      return (
                        <button
                          key={iso}
                          type="button"
                          onClick={() => setVisitDate(d)}
                          className="relative shrink-0 w-[72px] h-[92px] rounded-xl flex flex-col items-center justify-center transition-all"
                          style={{
                            backgroundColor: isSel ? "#083344" : "#F8FAFC",
                            color: isSel ? "#fff" : "#1F2937",
                            boxShadow: isSel ? "0 10px 24px rgba(8,51,68,0.35)" : "0 1px 2px rgba(0,0,0,0.04)",
                            border: isSel ? "2px solid #D4AF37" : "1px solid #E2E8F0",
                            fontFamily: "Cairo",
                          }}
                          data-testid={`khuta-date-${iso}`}
                        >
                          <span className="text-[11px] opacity-80">{ARABIC_DAYS[dow]}</span>
                          <span className="text-xl font-bold leading-none mt-1">{d.getDate()}</span>
                          <span className="text-[10px] opacity-80 mt-1">{ARABIC_MONTHS[d.getMonth()]}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                {visitDate && (
                  <p className="text-xs text-[#64748B] mt-2">
                    التاريخ المختار: {ARABIC_DAYS[visitDate.getDay()]} {visitDate.getDate()} {ARABIC_MONTHS[visitDate.getMonth()]} {visitDate.getFullYear()}
                    <span className="text-amber-700 font-medium"> (مبدئي بانتظار تأكيد المدرسة)</span>
                  </p>
                )}
                {dateList.length === 0 && (
                  <div
                    className="mt-3 rounded-lg px-3 py-2.5 text-xs leading-relaxed"
                    style={{ background: "#FFFBEB", border: "1px solid #D4AF37", color: "#92400E" }}
                  >
                    جميع التواريخ محجوزة حاليًا. يُرجى المحاولة لاحقًا.
                  </div>
                )}
              </div>

              {/* Honeypot */}
              <input
                type="text"
                name="website"
                value={form.website}
                onChange={(e) => updateForm({ website: e.target.value })}
                tabIndex={-1}
                autoComplete="off"
                style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none" }}
                aria-hidden="true"
              />

              {/* 1. Full name */}
              <div>
                <Label htmlFor="k-full-name" className="text-[#0B132B]">الاسم الكامل للمتقدمة أو قائدة الفريق *</Label>
                <Input
                  id="k-full-name"
                  value={form.full_name}
                  onChange={(e) => updateForm({ full_name: e.target.value })}
                  required
                  className="mt-2 h-11"
                  data-testid="khuta-full-name"
                />
              </div>

              {/* 2. Email */}
              <div>
                <Label htmlFor="k-email" className="text-[#0B132B]">البريد الإلكتروني للتواصل *</Label>
                <Input
                  id="k-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => updateForm({ email: e.target.value })}
                  required
                  className="mt-2 h-11"
                  data-testid="khuta-email"
                />
              </div>

              {/* 3. Phone (WhatsApp) */}
              <div>
                <Label htmlFor="k-phone" className="text-[#0B132B]">رقم الواتساب للتواصل *</Label>
                <Input
                  id="k-phone"
                  type="tel"
                  inputMode="tel"
                  dir="ltr"
                  placeholder="+9665xxxxxxxx"
                  value={form.phone}
                  onChange={(e) => updateForm({ phone: e.target.value })}
                  required
                  className="mt-2 h-11 text-left"
                  data-testid="khuta-phone"
                />
                <p className="text-[11px] text-[#64748B] mt-1">نحتاج رقم الواتساب للتنسيق معكِ قبل الزيارة.</p>
              </div>

              {/* 3. Participation type */}
              <div>
                <Label className="text-[#0B132B]">نوع المشاركة *</Label>
                <RadioGroup
                  value={form.participation_type}
                  onValueChange={(v) => updateForm({ participation_type: v })}
                  className="flex gap-6 mt-2"
                  dir="rtl"
                >
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="individual" id="k-indiv" data-testid="khuta-individual" />
                    <span>فردية</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="team" id="k-team" data-testid="khuta-team" />
                    <span>ضمن فريق</span>
                  </label>
                </RadioGroup>
              </div>

              {/* 4. Team members */}
              {form.participation_type === "team" && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-[#0B132B] font-semibold">أعضاء الفريق</Label>
                    <Button
                      type="button"
                      size="sm"
                      onClick={addTeamMember}
                      className="bg-[#164E63] hover:bg-[#0E7490] gap-1"
                      data-testid="khuta-add-member"
                    >
                      <Plus size={14} /> إضافة عضوة
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {form.team_members.map((m, i) => (
                      <div key={i} className="flex flex-col sm:flex-row gap-2 sm:gap-2 sm:items-end bg-white p-3 sm:p-0 sm:bg-transparent rounded-lg sm:rounded-none border sm:border-0 border-slate-200">
                        <div className="flex-1">
                          <Label className="text-xs text-[#475569]">الاسم الكامل *</Label>
                          <Input
                            value={m.name}
                            onChange={(e) => updateMember(i, { name: e.target.value })}
                            className="mt-1 h-11 sm:h-10"
                            data-testid={`khuta-member-name-${i}`}
                          />
                        </div>
                        <div className="flex-1">
                          <Label className="text-xs text-[#475569]">البريد الإلكتروني *</Label>
                          <Input
                            type="email"
                            value={m.email}
                            onChange={(e) => updateMember(i, { email: e.target.value })}
                            className="mt-1 h-11 sm:h-10"
                            data-testid={`khuta-member-email-${i}`}
                          />
                        </div>
                        {form.team_members.length > 1 && (
                          <Button
                            type="button"
                            size="sm"
                            variant="destructive"
                            onClick={() => removeTeamMember(i)}
                            className="h-11 sm:h-10 w-full sm:w-auto gap-1"
                            data-testid={`khuta-remove-member-${i}`}
                          >
                            <Trash2 size={14} />
                            <span className="sm:hidden">إزالة العضوة</span>
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 5. Graduation project */}
              <div>
                <Label className="text-[#0B132B]">هل المشاركة جزء من مشروع تخرج؟ *</Label>
                <RadioGroup
                  value={form.is_graduation_project ? "yes" : "no"}
                  onValueChange={(v) => updateForm({ is_graduation_project: v === "yes" })}
                  className="flex gap-6 mt-2"
                  dir="rtl"
                >
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="yes" id="k-grad-yes" data-testid="khuta-grad-yes" />
                    <span>نعم</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="no" id="k-grad-no" data-testid="khuta-grad-no" />
                    <span>لا</span>
                  </label>
                </RadioGroup>
                {form.is_graduation_project && (
                  <div className="mt-3">
                    <Label htmlFor="k-grad-name" className="text-xs text-[#475569]">اسم المشروع *</Label>
                    <Input
                      id="k-grad-name"
                      value={form.graduation_project_name}
                      onChange={(e) => updateForm({ graduation_project_name: e.target.value })}
                      className="mt-1"
                      data-testid="khuta-grad-project-name"
                    />
                  </div>
                )}
              </div>

              {/* 6. Work mode */}
              <div>
                <Label className="text-[#0B132B]">طبيعة العمل *</Label>
                <RadioGroup
                  value={form.work_mode}
                  onValueChange={(v) => updateForm({ work_mode: v })}
                  className="flex gap-6 mt-2"
                  dir="rtl"
                >
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="solo" id="k-solo" data-testid="khuta-solo" />
                    <span>فردي</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="supervised" id="k-sup" data-testid="khuta-supervised" />
                    <span>تحت إشراف</span>
                  </label>
                </RadioGroup>
                {form.work_mode === "supervised" && (
                  <div className="mt-3">
                    <Label htmlFor="k-sup-name" className="text-xs text-[#475569]">اسم المشرفة *</Label>
                    <Input
                      id="k-sup-name"
                      value={form.supervisor_name}
                      onChange={(e) => updateForm({ supervisor_name: e.target.value })}
                      className="mt-1"
                      data-testid="khuta-supervisor-name"
                    />
                  </div>
                )}
              </div>

              {/* 7. Workshop title */}
              <div>
                <Label htmlFor="k-title" className="text-[#0B132B]">عنوان الورشة أو البرنامج *</Label>
                <Input
                  id="k-title"
                  value={form.workshop_title}
                  onChange={(e) => updateForm({ workshop_title: e.target.value })}
                  required
                  className="mt-2 h-11"
                  data-testid="khuta-workshop-title"
                />
              </div>

              {/* 8. Workshop kind */}
              <div>
                <Label className="text-[#0B132B]">نوع المشاركة *</Label>
                <RadioGroup
                  value={form.workshop_kind}
                  onValueChange={(v) => updateForm({ workshop_kind: v })}
                  className="flex flex-wrap gap-6 mt-2"
                  dir="rtl"
                >
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="workshop" id="k-wk" data-testid="khuta-kind-workshop" />
                    <span>ورشة</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="program" id="k-pr" data-testid="khuta-kind-program" />
                    <span>برنامج</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="other" id="k-ot" data-testid="khuta-kind-other" />
                    <span>أخرى</span>
                  </label>
                </RadioGroup>
              </div>

              {/* 9. Short description */}
              <div>
                <Label htmlFor="k-desc" className="text-[#0B132B]">وصف مختصر للفكرة *</Label>
                <Textarea
                  id="k-desc"
                  value={form.short_description}
                  onChange={(e) => updateForm({ short_description: e.target.value })}
                  required
                  className="mt-2"
                  rows={3}
                  data-testid="khuta-short-description"
                />
              </div>

              {/* 10. Target audience */}
              <div>
                <Label htmlFor="k-audience" className="text-[#0B132B]">الفئة المستهدفة *</Label>
                <select
                  id="k-audience"
                  value={form.target_audience}
                  onChange={(e) => updateForm({ target_audience: e.target.value })}
                  className="mt-2 w-full h-11 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="khuta-target-audience"
                >
                  {AUDIENCE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              {/* 11. Objectives */}
              <div>
                <Label htmlFor="k-obj" className="text-[#0B132B]">أهداف الورشة أو البرنامج *</Label>
                <Textarea
                  id="k-obj"
                  value={form.objectives}
                  onChange={(e) => updateForm({ objectives: e.target.value })}
                  required
                  className="mt-2"
                  rows={3}
                  data-testid="khuta-objectives"
                />
              </div>

              {/* 12. Uniqueness */}
              <div>
                <Label htmlFor="k-uniq" className="text-[#0B132B]">ما الذي يميّز الورشة أو البرنامج؟ *</Label>
                <Textarea
                  id="k-uniq"
                  value={form.uniqueness}
                  onChange={(e) => updateForm({ uniqueness: e.target.value })}
                  required
                  className="mt-2"
                  rows={3}
                  data-testid="khuta-uniqueness"
                />
              </div>

              {/* 13. Student benefit */}
              <div>
                <Label htmlFor="k-ben" className="text-[#0B132B]">ما الفائدة التي ستعود على الطالبات؟ *</Label>
                <Textarea
                  id="k-ben"
                  value={form.student_benefit}
                  onChange={(e) => updateForm({ student_benefit: e.target.value })}
                  required
                  className="mt-2"
                  rows={3}
                  data-testid="khuta-benefit"
                />
              </div>

              {/* 14. Applicant goal */}
              <div>
                <Label htmlFor="k-goal" className="text-[#0B132B]">ما هدف المتقدمة من المشاركة في البرنامج؟ *</Label>
                <Textarea
                  id="k-goal"
                  value={form.applicant_goal}
                  onChange={(e) => updateForm({ applicant_goal: e.target.value })}
                  required
                  className="mt-2"
                  rows={3}
                  data-testid="khuta-applicant-goal"
                />
              </div>

              {/* 15. Additional notes */}
              <div>
                <Label htmlFor="k-notes" className="text-[#0B132B]">ملاحظات إضافية (اختياري)</Label>
                <Textarea
                  id="k-notes"
                  value={form.additional_notes}
                  onChange={(e) => updateForm({ additional_notes: e.target.value })}
                  className="mt-2"
                  rows={2}
                  data-testid="khuta-additional-notes"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting || (regStatus && !regStatus.open)}
                className="w-full h-12 text-base font-semibold rounded-xl text-[#083344] hover:brightness-105 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#D4AF37' }}
                data-testid="khuta-submit"
              >
                {submitting
                  ? "جاري الإرسال..."
                  : regStatus && !regStatus.open
                    ? (regStatus.today < regStatus.opens_at ? "التسجيل لم يُفتح بعد" : "التسجيل مغلق")
                    : "إرسال الطلب"}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default KhutaSadaDialog;
